/**
 * The fraud review queue and chargebacks — `/v1/admin/risk/*` (payments doc 15 §7, §8, §11).
 *
 * **Nothing here scores anything.** The score, the rules that fired and the SLA state all come
 * from the server; the console lists them and sends a reviewer's decision back.
 *
 * **A rule edit carries a reason** because it changes who gets flagged next, and the audit row is
 * the only place anyone will later learn why a threshold moved.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authedClient } from '../../auth/session';
import { keys } from '../queryClient';
import type { components } from '../../../api/generated/schema';

export type RiskCaseRow = components['schemas']['RiskCaseRow'];
export type RiskRuleRow = components['schemas']['RiskRuleRow'];
type RiskCaseList = components['schemas']['RiskCaseList'];
type RiskRuleList = components['schemas']['RiskRuleList'];
export type RiskCaseOpen = components['schemas']['RiskCaseOpen'];
export type RiskCaseDecide = components['schemas']['RiskCaseDecide'];
export type RiskRuleUpdate = components['schemas']['RiskRuleUpdate'];
export type ChargebackRow = components['schemas']['ChargebackRow'];
export type PackSection = components['schemas']['PackSection'];
type ChargebackList = components['schemas']['ChargebackList'];
type ChargebackDetail = components['schemas']['ChargebackDetail'];
type DocumentDownloadLink = components['schemas']['DocumentDownloadLink'];
type DocumentRenderingResponse = components['schemas']['DocumentRenderingResponse'];
export type RiskListKey = components['schemas']['RiskListKey'];
export type RiskBlockEntryRow = components['schemas']['RiskBlockEntryRow'];
type RiskBlockEntryList = components['schemas']['RiskBlockEntryList'];
type RiskBlockEntryAdd = components['schemas']['RiskBlockEntryAdd'];

export function useRiskCases(includeDecided: boolean, page = 1) {
  return useQuery({
    queryKey: keys.risk.cases(includeDecided, page),
    queryFn: () =>
      authedClient.request<RiskCaseList>(
        `/v1/admin/risk/cases?${includeDecided ? 'include_decided=true&' : ''}page=${page}`,
      ),
  });
}

export function useRiskRules() {
  return useQuery({
    queryKey: keys.risk.rules(),
    queryFn: () => authedClient.request<RiskRuleList>('/v1/admin/risk/rules'),
  });
}

export function useOpenRiskCase() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: RiskCaseOpen) =>
      authedClient.request<RiskCaseRow>('/v1/admin/risk/cases', { method: 'POST', body }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.risk.all() }),
  });
}

export function useDecideRiskCase() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ code, ...body }: RiskCaseDecide & { code: string }) =>
      authedClient.request<RiskCaseRow>(`/v1/admin/risk/cases/${code}/decide`, {
        method: 'POST',
        body,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.risk.all() }),
  });
}

export function useUpdateRiskRule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ ruleKey, ...body }: RiskRuleUpdate & { ruleKey: string }) =>
      authedClient.request<RiskRuleRow>(`/v1/admin/risk/rules/${ruleKey}`, {
        method: 'PATCH',
        body,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.risk.rules() }),
  });
}

export function useChargebacks(includeClosed: boolean, page = 1) {
  return useQuery({
    queryKey: keys.risk.chargebacks(includeClosed, page),
    queryFn: () =>
      authedClient.request<ChargebackList>(
        `/v1/admin/risk/chargebacks?${includeClosed ? 'include_closed=true&' : ''}page=${page}`,
      ),
  });
}

export function useChargeback(code: string) {
  return useQuery({
    queryKey: keys.risk.chargeback(code),
    queryFn: () => authedClient.request<ChargebackDetail>(`/v1/admin/risk/chargebacks/${code}`),
  });
}

/**
 * A short-lived link to one dispute's drawn pack (PDF + JSON zip). Until the `build_chargeback_packs`
 * cron has drawn it the server answers `202 rendering`, which is not an error: the pack is on its
 * way. A plain read asked for at the click, not a cached query, because the link expires.
 */
export function fetchChargebackPack(code: string) {
  return authedClient.request<DocumentDownloadLink | DocumentRenderingResponse>(
    `/v1/admin/risk/chargebacks/${code}/pack`,
  );
}

/**
 * The block lists checked at checkout (R122). `'all'` asks for both lists. A row carries only a
 * fingerprint of the value's HMAC: the raw install id or instrument token never comes back.
 */
export function useRiskBlockList(listKey: RiskListKey | 'all', includeRemoved: boolean) {
  return useQuery({
    queryKey: keys.risk.lists(listKey, includeRemoved),
    queryFn: () => {
      const query = new URLSearchParams();
      if (listKey !== 'all') query.set('list_key', listKey);
      if (includeRemoved) query.set('include_removed', 'true');
      const search = query.toString();
      return authedClient.request<RiskBlockEntryList>(`/v1/admin/risk/lists${search ? `?${search}` : ''}`);
    },
  });
}

/** Idempotent on the server: adding a listed value returns its row, and a removed one re-opens. */
export function useAddRiskBlockEntry() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: RiskBlockEntryAdd) =>
      authedClient.request<RiskBlockEntryRow>('/v1/admin/risk/lists', { method: 'POST', body }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.risk.all() }),
  });
}

export function useRemoveRiskBlockEntry() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ entryId, reason }: { entryId: number; reason: string }) =>
      authedClient.request<RiskBlockEntryRow>(`/v1/admin/risk/lists/${entryId}/remove`, {
        method: 'POST',
        body: { reason },
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.risk.all() }),
  });
}
